import { RocketLaunch } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
// import logo from '../../../assets/Img/ingles_parana_v2.png';
import { useInglesParanaKids, useStatusPesquisa } from '../hooks/useEvents';
import { InglesParanaKids } from '../types/events.types';

// ---------------------------------------------------------------------------
// Paleta baseada na logo Inglês Paraná Kids
// ---------------------------------------------------------------------------
const BRAND = {
  blue: '#1a5fa8',
  blueLight: '#e8f4fe',
  blueBorder: '#b5d4f4',
  green: '#1b8c5e',
  greenLight: '#e6f5ee',
  purple: '#534ab7',
  purpleLight: '#eeedfe',
  amber: '#b37c1a',
  amberLight: '#fffbf0',
  amberBorder: '#e8c86a',
};

// ---------------------------------------------------------------------------
// Tipos auxiliares
// ---------------------------------------------------------------------------
interface RuleItem {
  id: string;
  emoji: string;
  bg: string;
  text: React.ReactNode;
}

// interface TipItem {
//   id: string;
//   letter: string;
//   text: React.ReactNode;
// }

// ---------------------------------------------------------------------------
// Dados estáticos
// ---------------------------------------------------------------------------
const HEADER_DOTS = [
  { id: 'hd1', color: '#6fa8dc' },
  { id: 'hd2', color: '#9b59b6' },
  { id: 'hd3', color: '#5dade2' },
  { id: 'hd4', color: '#7fb3d3' },
  { id: 'hd5', color: '#76d7c4' },
  { id: 'hd6', color: '#9b59b6' },
  { id: 'hd7', color: '#5dade2' },
];

const RULES: RuleItem[] = [
  {
    id: 'rule-calendar',
    emoji: '✏️',
    bg: BRAND.blueLight,
    text: <>Clique na alternativa da sua resposta.</>,
  },
  {
    id: 'rule-school',
    emoji: '⏱️',
    bg: BRAND.greenLight,
    text: <>Faça com calma! Não há pressa!</>,
  },
  {
    id: 'rule-phone',
    emoji: '💡',
    bg: BRAND.purpleLight,
    text: <>Cada pergunta tem somente UMA resposta correta.</>,
  },
  {
    id: 'rule-send',
    emoji: '🚫',
    bg: BRAND.purpleLight,
    text: <>Não deixe nenhuma questão em branco - tente sempre o seu melhor!</>,
  },
  {
    id: 'rule-tab',
    emoji: '🖥️',
    bg: BRAND.blueLight,
    text: (
      <>
        Abra em apenas <strong>uma aba do navegador</strong>. Múltiplas abas
        podem causar instabilidade e suas respostas podem não ser salvas
        corretamente.
      </>
    ),
  },
];

// const TIPS: TipItem[] = [
//   {
//     id: 'tip-paper',
//     letter: 'a)',
//     text: (
//       <>
//         Use um <strong>papel em branco</strong> para anotar suas respostas
//         enquanto avança. Se precisar voltar a uma questão, suas escolhas estarão
//         salvas no papel.
//       </>
//     ),
//   },
//   {
//     id: 'tip-menu',
//     letter: 'a)',
//     text: (
//       <>
//         Após enviar, acesse o menu <strong>Visualizar</strong> no topo da página
//         para conferir todas as suas respostas.
//       </>
//     ),
//   },
//   {
//     id: 'tip-breathe',
//     letter: 'b)',
//     text: (
//       <>
//         Respira fundo, lê cada questão com calma e confia no que aprendeu. Você
//         está preparado(a)!
//       </>
//     ),
//   },
// ];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getInitials(nome: string): string {
  const parts = nome.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ---------------------------------------------------------------------------
// Estilos globais (injetados uma vez)
// ---------------------------------------------------------------------------
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
  .ipk-root * { font-family: 'Nunito', sans-serif !important; }
`;

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

function StudentCard({ data }: { data: InglesParanaKids }) {
  const firstName = data.nome?.split(' ')[0] ?? 'estudante';

  return (
    <Box
      sx={{
        background: '#fff',
        border: '0.5px solid #e0e0e0',
        borderRadius: '18px',
        p: 2,
        mb: 2,
      }}
    >
      {/* Avatar + nome */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: BRAND.blueLight,
            border: `2px solid ${BRAND.blueBorder}`,
            color: BRAND.blue,
            fontSize: 17,
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {getInitials(data.nome)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 15, fontWeight: 900, lineHeight: 1.2 }}>
            {data.nome}
          </Box>
          <Box sx={{ fontSize: 15, color: 'text.secondary' }}>
            {data.emailGoogle}
          </Box>
        </Box>
        <Box
          sx={{
            background: BRAND.greenLight,
            color: BRAND.green,
            fontSize: 11,
            fontWeight: 800,
            px: 1.5,
            py: 0.5,
            pl: 2,
            pr: 2,
            borderRadius: '20px',
          }}
        >
          ESTUDANTE
        </Box>
      </Box>

      <Box sx={{ borderTop: '0.5px solid #eee', mb: 1.5 }} />

      {/* Grid de campos */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.25,
        }}
      >
        {[
          {
            id: 'escola',
            label: 'Escola',
            value: `${data.descEscola} (${data.codMec})`,
          },
          {
            id: 'turma',
            label: 'Turma',
            value: `${data.descTurmaComp} (${data.codTurma})` || data.descTurma,
          },
          // { id: 'serie', label: 'Série', value: data.descSeriacao },
          // { id: 'turno', label: 'Turno', value: data.descTurno },
          {
            id: 'municipio',
            label: 'Cidade',
            value: `${data.descMun} (${data.codMun})`,
          },
          // { id: 'nre', label: 'NRE', value: data.descNre },
        ].map(({ id, label, value }) => (
          <Box key={id}>
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 800,
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mb: 0.25,
              }}
            >
              {label}
            </Box>
            <Box sx={{ fontSize: 14, fontWeight: 700 }}>{value || '—'}</Box>
          </Box>
        ))}
      </Box>

      {/* Saudação */}
      <Box
        sx={{
          mt: 3.5,
          background: BRAND.blueLight,
          borderRadius: '12px',
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ fontSize: 16, fontWeight: 600, color: BRAND.blue }}>
          👋 Olá, <b>{firstName}</b>! Este teste tem 30 questões. Leia cada
          questão com atenção e escolha a resposta correta: A, B, C ou D.
        </Box>
      </Box>
    </Box>
  );
}

function RuleCard({ rule }: { rule: RuleItem }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center', // 👈 era 'flex-start'
        gap: 1.5,
        background: '#fff',
        border: '0.5px solid #e0e0e0',
        borderRadius: '14px',
        p: '12px 14px',
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          background: rule.bg,
          fontSize: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {rule.emoji}
      </Box>
      <Box
        sx={{
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.7,
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          '-webkit-line-clamp': 2,
          overflow: 'hidden',
          '& strong': { fontWeight: 800 },
        }}
      >
        {rule.text}
      </Box>
    </Box>
  );
}

// function TipsBox() {
//   return (
//     <Box
//       sx={{
//         border: `1.5px solid ${BRAND.amberBorder}`,
//         borderLeft: `4px solid ${BRAND.amber}`,
//         borderRadius: '14px',
//         p: 2,
//         background: BRAND.amberLight,
//         mb: 2,
//       }}
//     >
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
//         <Box sx={{ fontSize: 18 }} aria-hidden="true">
//           💡
//         </Box>
//         <Box
//           sx={{
//             fontSize: 12,
//             fontWeight: 800,
//             color: BRAND.amber,
//             textTransform: 'uppercase',
//             letterSpacing: 0.5,
//           }}
//         >
//           Dicas importantes
//         </Box>
//       </Box>
//       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
//         {TIPS.map((tip) => (
//           <Box key={tip.id} sx={{ display: 'flex', gap: 1 }}>
//             <Box
//               sx={{
//                 fontSize: 14,
//                 fontWeight: 900,
//                 color: BRAND.amber,
//                 flexShrink: 0,
//               }}
//             >
//               {tip.letter}
//             </Box>
//             <Box
//               sx={{
//                 fontSize: 14,
//                 fontWeight: 600,
//                 lineHeight: 1.7,
//                 color: '#5a4000',
//                 '& strong': { fontWeight: 800 },
//               }}
//             >
//               {tip.text}
//             </Box>
//           </Box>
//         ))}
//       </Box>
//     </Box>
//   );
// }

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function InglesParanaKidsRegras() {
  const [agreed] = useState(true);
  const { student, isLoading: loading, error } = useInglesParanaKids();
  const {
    data,
    isLoading: statusLoading,
    error: statusError,
  } = useStatusPesquisa();
  const history = useHistory();

  return (
    <>
      {/* Injeção da fonte Nunito */}
      <style>{GLOBAL_STYLE}</style>

      <Box
        className="ipk-root"
        maxWidth={1200}
        mx="auto"
        px={2}
        py={3}
        sx={{ mt: 12, pb: 10 }}
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.green} 100%)`,
            borderRadius: { xs: '16px', sm: '20px' },
            p: { xs: 3, sm: 4, md: 5 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            // pb: 2,
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: { xs: 120, sm: 160 },
              height: { xs: 120, sm: 160 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.08)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: 40,
              width: { xs: 70, sm: 100 },
              height: { xs: 70, sm: 100 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.05)',
              pointerEvents: 'none',
            }}
          />

          {/* Content wrapper — stacks vertically on mobile, row on larger screens */}
          <Box
            sx={{
              height: '100px',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 2, sm: 3 },
            }}
          >
            {/* Logo */}
            {/* <Box
              component="img"
              src={logo}
              alt="Logo do evento"
              sx={{
                width: { xs: '170px', sm: '210px', md: '230px' },
                height: 'auto',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            /> */}
            {/* <Box sx={{ height: '100px' }} /> */}
            {/* Right side: badge + dots */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  px: 2,
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    fontSize: { xs: 11, sm: 13 },
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.92)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Diagnostic Exam | English Language · 5th Grade
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {HEADER_DOTS.map(({ id, color }) => (
                  <Box
                    key={id}
                    sx={{
                      width: { xs: 12, sm: 16 },
                      height: { xs: 12, sm: 16 },
                      borderRadius: '4px',
                      bgcolor: color,
                      opacity: 0.65,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Dados do aluno ───────────────────────────────────── */}
        {loading && (
          <Box textAlign="center" py={4}>
            <CircularProgress size={28} sx={{ color: BRAND.blue }} />
            <Box
              mt={1}
              sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 700 }}
            >
              Buscando seus dados...
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ borderRadius: '14px', mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {student && !loading && <StudentCard data={student} />}

        {/* ── Regras ───────────────────────────────────────────── */}
        <Box
          sx={{
            pt: 2,
            fontSize: 13,
            fontWeight: 800,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 1,
            mb: 1.25,
          }}
        >
          Regras do teste:
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {RULES.map((rule) => (
            <RuleCard key={rule.id} rule={rule} />
          ))}
        </Box>

        {/* ── Dicas ────────────────────────────────────────────── */}
        {/* <TipsBox /> */}

        {/* ── Concordância ─────────────────────────────────────── */}
        {/* <Box
          onClick={() => setAgreed((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: '14px',
            mb: 1.5,
            cursor: 'pointer',
            border: agreed
              ? `1.5px solid ${BRAND.green}`
              : '1.5px solid #e0e0e0',
            bgcolor: agreed ? BRAND.greenLight : '#fff',
            transition: 'all 0.2s',
            userSelect: 'none',
          }}
        >
          <Checkbox
            checked={agreed}
            onChange={(e) => {
              e.stopPropagation();
              setAgreed((v) => !v);
            }}
            sx={{
              color: BRAND.green,
              '&.Mui-checked': { color: BRAND.green },
              p: 0.5,
            }}
            checkedIcon={<CheckCircle />}
          />
          <Box sx={{ fontSize: 15, fontWeight: 700 }}>
            Li e concordo com todas as orientações acima
          </Box>
        </Box> */}

        {/* ── Botão iniciar ─────────────────────────────────────── */}
        {/* ── Botão iniciar ─────────────────────────────────────── */}
        {statusLoading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={28} sx={{ color: BRAND.blue }} />
          </Box>
        ) : statusError ? null : data?.isFinished === 1 ? ( // Erro na chamada → não mostra nada (ou pode mostrar um aviso discreto)
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 7 }}>
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                borderRadius: 2,
                backgroundColor: '#f0f7ff',
                border: '1px solid #90caf9',
                width: '100%',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: '#1565c0', fontWeight: 'bold', mb: 1 }}
              >
                ✅ Teste de nivelamento concluído!
              </Typography>
              <Typography variant="body1" sx={{ color: '#333' }}>
                Veja sua prova clicando em{' '}
                <Box
                  component="span"
                  sx={{ fontWeight: 'bold', color: '#1565c0' }}
                >
                  VISUALIZAR
                </Box>{' '}
                no menu superior direito.
              </Typography>
            </Box>
          </Box>
        ) : (
          // Sucesso + data vazia ou isFinished !== 1 → mostra o botão
          <Button
            fullWidth
            onClick={() =>
              history.push(
                `/evento/evtid=ECC5AA6E-EA1C-4F73-BA17-31140C7E248E/case=2`
              )
            }
            startIcon={<RocketLaunch />}
            variant={agreed ? 'contained' : 'outlined'}
            sx={{
              mt: 3,
              py: 1.75,
              borderRadius: '14px',
              fontWeight: 900,
              fontSize: 16,
              letterSpacing: 0.3,
              textTransform: 'none',
              fontFamily: "'Nunito', sans-serif",
              ...(agreed && !loading
                ? {
                    bgcolor: BRAND.blue,
                    color: '#fff',
                    boxShadow: `0 4px 14px rgba(26,95,168,0.3)`,
                    '&:not(:disabled):hover': {
                      bgcolor: '#154d8a',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 18px rgba(26,95,168,0.35)`,
                    },
                  }
                : {}),
            }}
          >
            Começar o meu teste
          </Button>
        )}
      </Box>
    </>
  );
}
